import React, { useEffect, useState, useCallback } from 'react';
import { Col, Text, Row, useDynamicResponsiveValue } from 'react-quick-style-components';
import { useNavFunc } from 'navigation';
import { useFocusEffect } from '@react-navigation/native';
import { IScreen } from 'type';
import { Table, Breadcrumbs } from 'components';
import { ASSETS, COLOR, fSize, ICON, SCREEN_NAME } from 'const';
import { Image, StyleSheet, useWindowDimensions, ActivityIndicator } from 'react-native';
import { Footer, Header, ChatBox } from 'components';
import moment from 'moment';
import Store from 'store';
import { VarHelper } from 'helpers';

const CaskTransferCompleted: IScreen = () => {
  const { width, height } = useWindowDimensions();
  const { route, navigate } = useNavFunc();
  const rV = useDynamicResponsiveValue();
  const bannerHeight = rV({ xs: 150, lg: 200, xxxl: 262 });

  const [{ }, eApi] = Store.Esign.createStore();
  const [{ listPages }, iApi] = Store.Info.createStore();
  const [{ }, cApi] = Store.Client.createStore();
  
  const [signedDocuments, setSignedDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string>('');
  const [sendingDraftId, setSendingDraftId] = useState<number | null>(null);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [shareDocumentId, setShareDocumentId] = useState<number | null>(null);
  const [shareEmails, setShareEmails] = useState<string>('');
  const [sharing, setSharing] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    iApi.getListPages();
  }, []);

  useEffect(() => {
    loadSignedDocuments();
  }, [currentPage]);

  // Reload data when screen comes into focus (user navigates back to this screen)
  useFocusEffect(
    useCallback(() => {
      console.log('🔄 CaskTransferCompleted screen focused, reloading data...');
      loadSignedDocuments();
      return () => {
        // Cleanup if needed
      };
    }, [currentPage])
  );

  const loadSignedDocuments = async () => {
    setLoading(true);
    setError('');
    const offset = (currentPage - 1) * pageSize;
    
    console.log('🔍 Loading esign documents...', { limit: pageSize, offset });
    
    const [res, err] = await eApi.getMyEsignRequests({
      limit: pageSize,
      offset: offset,
    });

    console.log('📦 API Response:', res);
    console.log('❌ API Error:', err);

    if (err) {
      console.error('Failed to load esign documents:', err);
      setError(err.toString());
      setSignedDocuments([]);
      setLoading(false);
      return;
    }

    if (res && res.success) {
      console.log('✅ Data received:', res.data);
      console.log('📊 Total records:', res.total);
      setSignedDocuments(res.data || []);
      setTotal(res.total || 0);
    } else {
      console.error('API returned unsuccessful:', res);
      setError(res?.message || 'Unknown error');
      setSignedDocuments([]);
    }
    setLoading(false);
  };

  const handleViewPDF = (recordId: number, pdfPath: string) => {
    if (!pdfPath) {
      alert('PDF not available');
      return;
    }
    
    // Navigate to detail page with document preview
    navigate('CaskTransferDetail', { id: recordId });
  };

  const handleDownloadPDF = (recordId: number, pdfUrl: string) => {
    if (!pdfUrl) {
      alert('PDF URL not available');
      return;
    }
    
    // Navigate to detail page (same as View, user can download from browser)
    navigate('CaskTransferDetail', { id: recordId });
  };

  const handleSendDraft = async (draft: any) => {
    if (!draft.cask_ids || draft.cask_ids.length === 0) {
      alert('This draft has no casks');
      return;
    }

    const confirmed = confirm(`Send this draft to ${draft.client_name} (${draft.client_email}) for signature?`);
    if (!confirmed) return;

    setSendingDraftId(draft.id);

    const [res, err] = await eApi.sendSignatureRequest({
      caskIds: draft.cask_ids,
      clientEmail: draft.client_email,
      clientName: draft.client_name,
      draftId: draft.id, // Pass draft ID to update existing record
    });

    setSendingDraftId(null);

    if (err) {
      alert(`Error: ${err}`);
      return;
    }

    if (res && res.success) {
      alert('Signature request sent successfully!');
      loadSignedDocuments(); // Reload to update status
    } else {
      alert(res?.message || 'Failed to send signature request');
    }
  };

  const handleDeleteDraft = async (draft: any) => {
    const confirmed = confirm(`Are you sure you want to delete this draft for ${draft.client_name}?`);
    if (!confirmed) return;

    setLoading(true);

    const [res, err] = await eApi.deleteDraft(draft.id);

    if (err) {
      setLoading(false);
      alert(`Error: ${err}`);
      return;
    }

    if (res && res.success) {
      alert('Draft deleted successfully!');
      // Wait a bit for backend to process, then reload data
      setTimeout(async () => {
        await loadSignedDocuments();
        setLoading(false);
      }, 500);
    } else {
      setLoading(false);
      alert(res?.message || 'Failed to delete draft');
    }
  };

  const handleOpenShareModal = (doc: any) => {
    setShareDocumentId(doc.id);
    setShareEmails('');
    setShareModalVisible(true);
  };

  const handleCloseShareModal = () => {
    setShareModalVisible(false);
    setShareDocumentId(null);
    setShareEmails('');
  };

  const handleShareDocument = async () => {
    if (!shareEmails.trim()) {
      alert('Please enter at least one email address');
      return;
    }

    // Parse emails (comma or semicolon separated)
    const emailList = shareEmails
      .split(/[,;]/)
      .map(e => e.trim())
      .filter(e => e.length > 0);

    if (emailList.length === 0) {
      alert('Please enter valid email addresses');
      return;
    }

    setSharing(true);

    const [res, err] = await eApi.shareDocument({
      esignId: shareDocumentId!,
      recipientEmails: emailList,
    });

    setSharing(false);

    if (err) {
      alert(`Error: ${err}`);
      return;
    }

    if (res && res.success) {
      alert(res.message || 'Document shared successfully!');
      handleCloseShareModal();
    } else {
      alert(res?.message || 'Failed to share document');
    }
  };


  const pageData = listPages.find(val => val.Title === 'Portfolio Details');
  const pageDataImages = VarHelper.imgApiStringifyGetAll(pageData?.Images);

  const totalPages = Math.ceil(total / pageSize);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <Row center marginTop={30} marginBottom={20}>
        <Text
          onPress={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
          style={{
            padding: 10,
            marginHorizontal: 5,
            cursor: currentPage > 1 ? 'pointer' : 'not-allowed',
            opacity: currentPage > 1 ? 1 : 0.5,
          }}
        >
          ← Previous
        </Text>
        <Text marginHorizontal={20}>
          Page {currentPage} of {totalPages} (Total: {total} documents)
        </Text>
        <Text
          onPress={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
          style={{
            padding: 10,
            marginHorizontal: 5,
            cursor: currentPage < totalPages ? 'pointer' : 'not-allowed',
            opacity: currentPage < totalPages ? 1 : 0.5,
          }}
        >
          Next →
        </Text>
      </Row>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Col center paddingVertical={60}>
          <ActivityIndicator size="large" color={COLOR.PRIMARY} />
          <Text marginTop={20} fontSize={16}>Loading documents...</Text>
        </Col>
      );
    }

    if (error) {
      return (
        <Col center paddingVertical={60}>
          <Text fontSize={18} color="red">Error loading documents</Text>
          <Text fontSize={14} color="#666" marginTop={10}>{error}</Text>
          <Text 
            fontSize={14} 
            color={COLOR.PRIMARY} 
            marginTop={20}
            onPress={loadSignedDocuments}
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            Retry
          </Text>
        </Col>
      );
    }

    if (signedDocuments.length === 0) {
      return (
        <Col>
          <Text bold fontSize20 marginLeft30>Cask Transfer Documents</Text>
          <Text center margin30>No documents found</Text>
        </Col>
      );
    }

    return (
      <Col>
        <Text bold fontSize20 marginLeft30 marginBottom={20}>Cask Transfer Documents</Text>
        <Table
          columns={[
            { key: 'client_name', title: 'CLIENT NAME', flex: 2, center: true },
            { key: 'client_email', title: 'EMAIL', flex: 2, center: true },
            { key: 'cask_count', title: 'CASKS', flex: 1, center: true },
            { key: 'status_formatted', title: 'STATUS', flex: 1, center: true },
            { key: 'created_at_formatted', title: 'CREATED DATE', flex: 2, center: true },
            { key: 'signed_at_formatted', title: 'SIGNED DATE', flex: 2, center: true },
            { key: 'actions', title: 'ACTIONS', flex: 2, center: true },
          ]}
          data={signedDocuments.map(doc => ({
            ...doc,
            client_name: doc.client_name,
            client_email: doc.client_email,
            cask_count: Array.isArray(doc.cask_ids) ? doc.cask_ids.length : 0,
            status_formatted: (() => {
              if (doc.status === 'draft') return 'Draft';
              if (doc.status === 'signed') return 'Signed';
              if (doc.status === 'completed') return 'Completed';
              return 'Unknown';
            })(),
            created_at_formatted: doc.created_at ? moment(doc.created_at).format('DD/MM/YYYY HH:mm') : '-',
            signed_at_formatted: doc.signed_at ? moment(doc.signed_at).format('DD/MM/YYYY HH:mm') : '-',
            actions: () => {
              if (doc.status === 'draft') {
                return (
                  <Row center>
                    {doc.pdf_path && (
                      <>
                        <a 
                          onClick={() => handleViewPDF(doc.id, doc.pdf_path)}
                          style={{ 
                            cursor: 'pointer',
                            color: COLOR.MAIN,
                            textDecoration: 'underline',
                            fontSize: 13,
                            marginRight: 10,
                          }}
                          title="View PDF Preview"
                        >
                          View
                        </a>
                        <Text style={{ color: '#ccc', marginRight: 10 }}>|</Text>
                      </>
                    )}
                    <a 
                      onClick={() => handleSendDraft(doc)}
                      style={{ 
                        cursor: sendingDraftId === doc.id ? 'not-allowed' : 'pointer',
                        color: COLOR.MAIN,
                        textDecoration: 'underline',
                        fontSize: 13,
                        marginRight: 10,
                      }}
                      title={sendingDraftId === doc.id ? 'Sending...' : 'Send for Signature'}
                    >
                      {sendingDraftId === doc.id ? 'Sending...' : 'Send'}
                    </a>
                    <Text style={{ color: '#ccc', marginRight: 10 }}>|</Text>
                    <a 
                      onClick={() => handleDeleteDraft(doc)}
                      style={{ 
                        cursor: 'pointer',
                        color: '#d32f2f',
                        textDecoration: 'underline',
                        fontSize: 13,
                      }}
                      title="Delete Draft"
                    >
                      Delete
                    </a>
                  </Row>
                );
              } else {
                return (
                  <Row center>
                    <a 
                      onClick={() => handleViewPDF(doc.id, doc.signed_pdf_path || doc.pdf_path)}
                      style={{ 
                        cursor: 'pointer',
                        color: COLOR.MAIN,
                        textDecoration: 'underline',
                        fontSize: 13,
                        marginRight: 10,
                      }}
                      title="View PDF"
                    >
                      View
                    </a>
                    <Text style={{ color: '#ccc', marginRight: 10 }}>|</Text>
                    <a 
                      onClick={() => handleDownloadPDF(doc.id, doc.signed_pdf_path || doc.pdf_path)}
                      style={{ 
                        cursor: 'pointer',
                        color: COLOR.MAIN,
                        textDecoration: 'underline',
                        fontSize: 13,
                        marginRight: doc.status === 'completed' ? 10 : 0,
                      }}
                      title="Download PDF"
                    >
                      Download
                    </a>
                    {doc.status === 'completed' && (
                      <>
                        <Text style={{ color: '#ccc', marginRight: 10 }}>|</Text>
                        <a 
                          onClick={() => handleOpenShareModal(doc)}
                          style={{ 
                            cursor: 'pointer',
                            color: COLOR.MAIN,
                            textDecoration: 'underline',
                            fontSize: 13,
                          }}
                          title="Share via Email"
                        >
                          Share
                        </a>
                      </>
                    )}
                  </Row>
                );
              }
            },
          }))}
          breakpointToScroll="sm"
          style={{ margin: 20 }}
        />
        {renderPagination()}
      </Col>
    );
  };

  const ScreenContainer = React.Fragment;

  return (
    <Col flex1 backgroundColor={COLOR.BG}>
      <ScreenContainer>
        <Header />
        <Col middle height={bannerHeight}>
          <Image 
            source={pageDataImages.length > 0 ? { uri: pageDataImages[0] } : ASSETS.BANNER_ACCOUNT} 
            style={[{ height: bannerHeight }, styles.banner]} 
          />
          <Col zIndex99 center>
            <Text 
              fontSize={fSize(rV, 52)} 
              colorWhite 
              fontFamily='PlayfairDisplay_600SemiBold' 
              marginBottom={10}
            >
              Cask Transfer
            </Text>
            <Col alignSelf="center">
              <ICON.IcPattern />
            </Col>
          </Col>
        </Col>
        <Col 
          style={{ minHeight: height - 300 }} 
          marginVertical={rV({ xs: 16, lg: 30, xxxl: 40 })} 
          paddingHorizontal={rV({ xs: 4, lg: 20, xxxl: 40 })}
        >
          <Breadcrumbs
            title="Certificates > Cask Transfer"
            style={{
              marginLeft: rV({ xs: 4, lg: 20, xxxl: 20 }),
              marginBottom: 20,
            }}
          />
          
          {renderContent()}
        </Col>
        <Footer />
      </ScreenContainer>
      <ChatBox />
      
      {/* Share Modal */}
      {shareModalVisible && (
        <Col
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onPress={handleCloseShareModal}
        >
          <Col
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              padding: 30,
              maxWidth: 500,
              width: '90%',
            }}
            onPress={(e) => e.stopPropagation()}
          >
            <Text fontSize={20} bold marginBottom={20}>Share Document via Email</Text>
            
            <Text fontSize={14} marginBottom={10}>
              Enter email addresses (separated by comma or semicolon):
            </Text>
            
            <textarea
              value={shareEmails}
              onChange={(e) => setShareEmails(e.target.value)}
              placeholder="example1@email.com, example2@email.com"
              style={{
                width: '100%',
                minHeight: 100,
                padding: 10,
                fontSize: 14,
                border: '1px solid #ccc',
                borderRadius: 4,
                fontFamily: 'Arial, sans-serif',
                resize: 'vertical',
              }}
              disabled={sharing}
            />
            
            <Row justifyContent="flex-end" marginTop={20}>
              <button
                onClick={handleCloseShareModal}
                disabled={sharing}
                style={{
                  padding: '10px 20px',
                  marginRight: 10,
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  backgroundColor: 'white',
                  cursor: sharing ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleShareDocument}
                disabled={sharing}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: 4,
                  backgroundColor: COLOR.MAIN,
                  color: 'white',
                  cursor: sharing ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  opacity: sharing ? 0.6 : 1,
                }}
              >
                {sharing ? 'Sending...' : 'Send Email'}
              </button>
            </Row>
          </Col>
        </Col>
      )}
    </Col>
  );
};

const styles = StyleSheet.create({
  banner: {
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
});

CaskTransferCompleted.routeInfo = {
  title: 'Cask Transfer',
  path: '/documents/cask-transfer'
};

export default CaskTransferCompleted;
